// web/src/pages/Admin.tsx
// FRAME Admin Panel — Sandstone Cloud internal dashboard
// Dark navy theme, inline styles, no external UI libraries
import React, { useState, useMemo, useEffect } from 'react';

// ─── Design tokens ──────────────────────────────────────────
const T = {
  navy: '#0f172a',
  surface: '#1e293b',
  surfaceHover: '#334155',
  blue: '#2563eb',
  blueLight: '#3b82f6',
  amber: '#d4a574',
  amberLight: '#e8c9a0',
  textPrimary: '#f8fafc',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  border: '#334155',
  error: '#ef4444',
  success: '#22c55e',
} as const;

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const WHITELIST = ['jemell@sandstonecloud.com', 'oliver@sandstonecloud.com'];

// ─── Inject global styles ───────────────────────────────────
const injectAdminCSS = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('frame-admin-css')) return;
  const style = document.createElement('style');
  style.id = 'frame-admin-css';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: ${T.navy}; }
    input::placeholder { color: ${T.textMuted}; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: ${T.navy}; }
    ::-webkit-scrollbar-thumb { background: ${T.surfaceHover}; border-radius: 3px; }
  `;
  document.head.appendChild(style);
};

// ─── Mock data ──────────────────────────────────────────────
const MOCK_STATS = [
  { label: 'Total Users', value: '12,847', icon: '👤', delta: '+3.2%' },
  { label: 'Active Subscriptions', value: '4,291', icon: '⭐', delta: '+7.8%' },
  { label: 'Photos Scored Today', value: '38,402', icon: '📷', delta: '+12.1%' },
  { label: 'Revenue MTD', value: '$42,850', icon: '💰', delta: '+5.4%' },
];

const MOCK_USERS = [
  { email: 'sarah.chen@gmail.com', tier: 'gold' as const, photosScored: 2841, joined: '2025-08-14', status: 'active' as const },
  { email: 'marcus.j@outlook.com', tier: 'silver' as const, photosScored: 1203, joined: '2025-10-02', status: 'active' as const },
  { email: 'emma.wilson@yahoo.com', tier: 'free' as const, photosScored: 87, joined: '2026-01-19', status: 'active' as const },
  { email: 'alex.kumar@proton.me', tier: 'gold' as const, photosScored: 5620, joined: '2025-06-30', status: 'active' as const },
  { email: 'lisa.m@icloud.com', tier: 'silver' as const, photosScored: 445, joined: '2025-12-11', status: 'inactive' as const },
  { email: 'david.park@gmail.com', tier: 'free' as const, photosScored: 12, joined: '2026-03-28', status: 'active' as const },
];

const MOCK_CHART_DATA = [
  { day: 'Mon', value: 4200 },
  { day: 'Tue', value: 5800 },
  { day: 'Wed', value: 3900 },
  { day: 'Thu', value: 7100 },
  { day: 'Fri', value: 6400 },
  { day: 'Sat', value: 8200 },
  { day: 'Sun', value: 5500 },
];

const SYSTEM_SERVICES = [
  { name: 'API Gateway', status: 'healthy' as const },
  { name: 'Scoring Engine', status: 'healthy' as const },
  { name: 'CDN (CloudFront)', status: 'healthy' as const },
  { name: 'Database (DynamoDB)', status: 'healthy' as const },
];

type NavSection = 'dashboard' | 'users' | 'analytics' | 'health';

// ─── Component ──────────────────────────────────────────────
export function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeSection, setActiveSection] = useState<NavSection>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    injectAdminCSS();
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!email || !password) {
      setLoginError('Please enter both email and password.');
      return;
    }
    if (!email.endsWith('@sandstonecloud.com') && !WHITELIST.includes(email.toLowerCase())) {
      setLoginError('Access restricted to Sandstone Cloud team members.');
      return;
    }
    if (password.length < 4) {
      setLoginError('Invalid credentials.');
      return;
    }
    setIsAuthenticated(true);
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return MOCK_USERS;
    const q = searchQuery.toLowerCase();
    return MOCK_USERS.filter(u =>
      u.email.toLowerCase().includes(q) ||
      u.tier.toLowerCase().includes(q) ||
      u.status.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const chartMax = Math.max(...MOCK_CHART_DATA.map(d => d.value));

  // ─── Login gate ───────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh', background: T.navy, display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontFamily: FONT,
        padding: 20,
      }}>
        <div style={{
          background: T.surface, borderRadius: 16, padding: 40,
          width: '100%', maxWidth: 420, border: `1px solid ${T.border}`,
        }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              fontSize: 32, fontWeight: 700, color: T.textPrimary,
              letterSpacing: 3, marginBottom: 4,
            }}>
              FRAME
            </div>
            <div style={{
              fontSize: 13, color: T.amber, fontWeight: 500,
              letterSpacing: 1.5, textTransform: 'uppercase',
            }}>
              Admin Panel
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>
              Sandstone Cloud Internal
            </div>
          </div>

          <form onSubmit={handleLogin}>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, color: T.textSecondary, display: 'block', marginBottom: 6 }}>
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@sandstonecloud.com"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: `1px solid ${T.border}`, background: T.navy,
                  color: T.textPrimary, fontSize: 14, fontFamily: FONT,
                  outline: 'none',
                }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 24 }}>
              <span style={{ fontSize: 13, color: T.textSecondary, display: 'block', marginBottom: 6 }}>
                Password
              </span>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: `1px solid ${T.border}`, background: T.navy,
                  color: T.textPrimary, fontSize: 14, fontFamily: FONT,
                  outline: 'none',
                }}
              />
            </label>

            {loginError && (
              <div style={{
                color: T.error, fontSize: 13, marginBottom: 16,
                padding: '8px 12px', background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: 8,
              }}>
                {loginError}
              </div>
            )}

            <button type="submit" style={{
              width: '100%', padding: '12px 0', borderRadius: 8,
              background: T.blue, color: T.textPrimary, fontSize: 15,
              fontWeight: 600, border: 'none', cursor: 'pointer',
              fontFamily: FONT, transition: 'background 0.2s',
            }}>
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Sidebar nav items ────────────────────────────────────
  const navItems: { key: NavSection; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: '📊' },
    { key: 'users', label: 'Users', icon: '👥' },
    { key: 'analytics', label: 'Analytics', icon: '📈' },
    { key: 'health', label: 'System Health', icon: '🔧' },
  ];

  const tierBadge = (tier: string): React.CSSProperties => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
    fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
    background: tier === 'gold' ? 'rgba(212, 165, 116, 0.15)' :
                tier === 'silver' ? 'rgba(148, 163, 184, 0.15)' :
                'rgba(100, 116, 139, 0.1)',
    color: tier === 'gold' ? T.amber :
           tier === 'silver' ? T.textSecondary :
           T.textMuted,
  });

  const statusDot = (status: string): React.CSSProperties => ({
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: status === 'active' || status === 'healthy' ? T.success : T.error,
    marginRight: 6,
  });

  // ─── Sidebar ──────────────────────────────────────────────
  const sidebar = (
    <div style={{
      width: 240, background: T.surface, height: '100vh',
      borderRight: `1px solid ${T.border}`, display: 'flex',
      flexDirection: 'column', position: isMobile ? 'fixed' : 'relative',
      top: 0, left: 0, zIndex: 100,
      transform: isMobile && !sidebarOpen ? 'translateX(-100%)' : 'translateX(0)',
      transition: 'transform 0.25s ease',
    }}>
      {/* Brand */}
      <div style={{
        padding: '24px 20px 20px', borderBottom: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.textPrimary, letterSpacing: 2 }}>
          FRAME
        </div>
        <div style={{ fontSize: 11, color: T.amber, fontWeight: 500, letterSpacing: 1.2, marginTop: 2 }}>
          ADMIN CONSOLE
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 8px' }}>
        {navItems.map(item => {
          const active = activeSection === item.key;
          return (
            <button
              key={item.key}
              onClick={() => {
                setActiveSection(item.key);
                if (isMobile) setSidebarOpen(false);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontFamily: FONT,
                fontSize: 14, fontWeight: active ? 600 : 400,
                background: active ? 'rgba(37, 99, 235, 0.12)' : 'transparent',
                color: active ? T.blueLight : T.textSecondary,
                textAlign: 'left', transition: 'all 0.15s',
                marginBottom: 2,
              }}
            >
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              {item.label}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{
        padding: '16px 20px', borderTop: `1px solid ${T.border}`,
        fontSize: 12, color: T.textMuted,
      }}>
        <div style={{ marginBottom: 4 }}>{email}</div>
        <button
          onClick={() => { setIsAuthenticated(false); setEmail(''); setPassword(''); }}
          style={{
            background: 'none', border: 'none', color: T.textMuted,
            cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: FONT,
            textDecoration: 'underline',
          }}
        >
          Sign out
        </button>
      </div>
    </div>
  );

  // ─── Mobile overlay ───────────────────────────────────────
  const overlay = isMobile && sidebarOpen ? (
    <div
      onClick={() => setSidebarOpen(false)}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 99,
      }}
    />
  ) : null;

  // ─── Section renderers ────────────────────────────────────
  const renderDashboard = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>
        Dashboard Overview
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 16,
      }}>
        {MOCK_STATS.map(stat => (
          <div key={stat.label} style={{
            background: T.surface, borderRadius: 12, padding: 20,
            border: `1px solid ${T.border}`, transition: 'border-color 0.2s',
            cursor: 'default',
          }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = T.surfaceHover)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = T.border)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{stat.label}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: T.textPrimary }}>{stat.value}</div>
              </div>
              <span style={{ fontSize: 24 }}>{stat.icon}</span>
            </div>
            <div style={{
              marginTop: 12, fontSize: 12, color: T.success, fontWeight: 500,
            }}>
              {stat.delta} from last month
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderUsers = () => (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary }}>
          User Management
        </h2>
        <input
          type="text"
          placeholder="Search users..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            padding: '8px 14px', borderRadius: 8, border: `1px solid ${T.border}`,
            background: T.surface, color: T.textPrimary, fontSize: 13,
            fontFamily: FONT, outline: 'none', width: isMobile ? '100%' : 260,
          }}
        />
      </div>

      <div style={{
        background: T.surface, borderRadius: 12, border: `1px solid ${T.border}`,
        overflow: 'auto',
      }}>
        <table style={{
          width: '100%', borderCollapse: 'collapse', fontSize: 13,
          minWidth: 640,
        }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {['Email', 'Tier', 'Photos Scored', 'Joined', 'Status', 'Actions'].map(h => (
                <th key={h} style={{
                  padding: '12px 16px', textAlign: 'left', color: T.textMuted,
                  fontWeight: 500, fontSize: 12, textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.email} style={{ borderBottom: `1px solid ${T.border}` }}>
                <td style={{ padding: '12px 16px', color: T.textPrimary }}>
                  {user.email}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={tierBadge(user.tier)}>{user.tier}</span>
                </td>
                <td style={{ padding: '12px 16px', color: T.textSecondary }}>
                  {user.photosScored.toLocaleString()}
                </td>
                <td style={{ padding: '12px 16px', color: T.textSecondary }}>
                  {user.joined}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={statusDot(user.status)} />
                    <span style={{
                      color: user.status === 'active' ? T.success : T.textMuted,
                      textTransform: 'capitalize', fontSize: 12,
                    }}>
                      {user.status}
                    </span>
                  </span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{
                      padding: '4px 12px', borderRadius: 6, fontSize: 12,
                      border: `1px solid ${T.border}`, background: 'transparent',
                      color: T.textSecondary, cursor: 'pointer', fontFamily: FONT,
                    }}>
                      View
                    </button>
                    <button style={{
                      padding: '4px 12px', borderRadius: 6, fontSize: 12,
                      border: 'none', background: T.blue, color: T.textPrimary,
                      cursor: 'pointer', fontFamily: FONT,
                    }}>
                      Manage
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={6} style={{
                  padding: 32, textAlign: 'center', color: T.textMuted,
                }}>
                  No users match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>
        Analytics
      </h2>
      <div style={{
        background: T.surface, borderRadius: 12, padding: 24,
        border: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.textPrimary, marginBottom: 4 }}>
          Scores by Day
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 24 }}>
          Last 7 days — total photos scored
        </div>

        {/* CSS-only bar chart */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', gap: isMobile ? 8 : 16,
          height: 200, paddingBottom: 28, position: 'relative',
        }}>
          {MOCK_CHART_DATA.map(d => {
            const pct = (d.value / chartMax) * 100;
            return (
              <div key={d.day} style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', height: '100%', justifyContent: 'flex-end',
              }}>
                <div style={{
                  fontSize: 11, color: T.textSecondary, marginBottom: 6,
                }}>
                  {d.value.toLocaleString()}
                </div>
                <div style={{
                  width: '100%', maxWidth: 48, borderRadius: '6px 6px 0 0',
                  background: `linear-gradient(180deg, ${T.blueLight}, ${T.blue})`,
                  height: `${pct}%`, minHeight: 4, transition: 'height 0.4s ease',
                }} />
                <div style={{
                  fontSize: 12, color: T.textMuted, marginTop: 8,
                  position: 'absolute', bottom: 0,
                }}>
                  {d.day}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderHealth = () => (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: T.textPrimary, marginBottom: 20 }}>
        System Health
      </h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 16,
      }}>
        {SYSTEM_SERVICES.map(svc => (
          <div key={svc.name} style={{
            background: T.surface, borderRadius: 12, padding: 20,
            border: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: T.textPrimary }}>
                {svc.name}
              </div>
              <div style={{
                fontSize: 12, color: svc.status === 'healthy' ? T.success : T.error,
                marginTop: 4, textTransform: 'capitalize',
              }}>
                {svc.status === 'healthy' ? 'Operational' : 'Degraded'}
              </div>
            </div>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: svc.status === 'healthy' ? T.success : T.error,
              boxShadow: svc.status === 'healthy'
                ? `0 0 8px ${T.success}40`
                : `0 0 8px ${T.error}40`,
            }} />
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 20, background: T.surface, borderRadius: 12,
        padding: 20, border: `1px solid ${T.border}`,
      }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: T.textPrimary, marginBottom: 12 }}>
          Platform Info
        </div>
        {[
          ['Environment', 'Production'],
          ['Region', 'us-east-1'],
          ['API Version', 'v1.4.2'],
          ['Last Deploy', '2026-03-31 14:22 UTC'],
        ].map(([k, v]) => (
          <div key={k} style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '8px 0', borderBottom: `1px solid ${T.border}`,
            fontSize: 13,
          }}>
            <span style={{ color: T.textMuted }}>{k}</span>
            <span style={{ color: T.textSecondary }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const sectionMap: Record<NavSection, () => React.ReactNode> = {
    dashboard: renderDashboard,
    users: renderUsers,
    analytics: renderAnalytics,
    health: renderHealth,
  };

  // ─── Main layout ──────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', minHeight: '100vh', background: T.navy,
      fontFamily: FONT, color: T.textPrimary,
    }}>
      {overlay}
      {sidebar}

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <header style={{
          height: 56, display: 'flex', alignItems: 'center',
          padding: '0 24px', borderBottom: `1px solid ${T.border}`,
          background: T.surface, gap: 16,
        }}>
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: T.textPrimary, fontSize: 20, padding: 4,
              }}
              aria-label="Toggle menu"
            >
              ☰
            </button>
          )}
          <div style={{ fontSize: 15, fontWeight: 500, color: T.textSecondary }}>
            {navItems.find(n => n.key === activeSection)?.label}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{
            fontSize: 12, color: T.textMuted,
            display: isMobile ? 'none' : 'block',
          }}>
            Sandstone Cloud
          </div>
        </header>

        {/* Content area */}
        <main style={{
          flex: 1, padding: isMobile ? 16 : 32, overflowY: 'auto',
        }}>
          {sectionMap[activeSection]()}
        </main>
      </div>
    </div>
  );
}
